import { ArrowUpRight, CalendarRange, Mail, RefreshCw, Sun, Sunset } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

import { api } from "@/api";
import { errText } from "@/components/pipeline/utils";
import { Delta } from "@/components/cockpit/StatsPanel";

const KINDS = [["morning", "Briefing du matin", Sun], ["evening", "Bilan de fin de journée", Sunset], ["period", "Bilan sur une période", CalendarRange]];
const iso = (d) => d.toISOString().slice(0, 10);

// Bilan IA sur des dates choisies
const PeriodBrief = () => {
  const [range, setRange] = useState(() => ({ from: iso(new Date(Date.now() - 6 * 864e5)), to: iso(new Date()) }));
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try { setRes((await api.post("/cockpit/briefing/period", { date_from: range.from, date_to: range.to })).data); } catch (e) { toast.error(errText(e)); } finally { setBusy(false); }
  };
  return (
    <div className="bf-body bf-period" data-testid="ck-period">
      <div className="bf-period__row">
        <label>Du <input type="date" value={range.from} max={range.to} onChange={(e) => setRange({ ...range, from: e.target.value })} data-testid="ck-period-from" /></label>
        <label>au <input type="date" value={range.to} min={range.from} onChange={(e) => setRange({ ...range, to: e.target.value })} data-testid="ck-period-to" /></label>
        <button type="button" className="bf-gen" disabled={busy || !range.from || !range.to} onClick={run} data-testid="ck-period-generate"><RefreshCw size={13} className={busy ? "spin" : ""} /> {busy ? "Rédaction…" : "Générer le bilan"}</button>
      </div>
      {res?.error && <p className="pl-warning">{res.error}</p>}
      {res && <ul data-testid="ck-period-lines">{res.lines.map((l) => <li key={l}>{l}</li>)}</ul>}
      {!res && <p className="bf-empty">Choisissez les dates puis générez : l'IA résume les appels, décrochés, fiches touchées, rendez-vous et envois de la période.</p>}
    </div>
  );
};

const frDay = (iso) => new Date(iso).toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", timeZone: "Europe/Paris" });

const WeekRecap = ({ week }) => (
  <div className="bf-week" data-testid="ck-briefing-week">
    <h4>{`Semaine du ${frDay(week.range[0])} au ${frDay(new Date(new Date(week.range[1]) - 1))} vs semaine précédente`}</h4>
    <div className="bf-week-grid">
      {week.rows.map((r) => (
        <div key={r.key} data-testid={`ck-week-${r.key}`}><span>{r.label}</span><strong>{r.current}</strong><small><Delta cur={r.current} prev={r.previous} points={r.key === "reach_rate"} testid={`ck-week-delta-${r.key}`} /> <em>{r.previous}</em></small></div>
      ))}
    </div>
    {week.lines?.length > 0 && <ul className="bf-week-lines" data-testid="ck-briefing-week-lines">{week.lines.map((l) => <li key={l}>{l}</li>)}</ul>}
  </div>
);

export const Skeleton = ({ lines = 3, testid }) => (
  <div className="ck-skel" aria-busy="true" aria-label="Chargement" data-testid={testid}>{Array.from({ length: lines }, (_, i) => <span key={i} style={{ width: `${92 - ((i * 17) % 40)}%` }} />)}</div>
);

// Briefing du matin / bilan du soir, en tête du Cockpit
export const BriefingCard = () => {
  const [kind, setKind] = useState(() => (Number(new Date().toLocaleString("fr-FR", { hour: "2-digit", hour12: false, timeZone: "Europe/Paris" })) >= 18 ? "evening" : "morning"));
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => (kind === "period" ? Promise.resolve() : api.get("/cockpit/briefing", { params: { kind } })).then((r) => r && setData(r.data)).catch(() => setData({})), [kind]);
  useEffect(() => { load(); }, [load]);
  const generate = async () => {
    setBusy(true);
    try { const { data: b } = await api.post("/cockpit/briefing/generate", { kind }); setData((d) => ({ ...d, briefing: b })); } catch (e) { toast.error(errText(e)); } finally { setBusy(false); }
  };
  const setEmail = (email) => api.put("/cockpit/briefing-prefs", { ...data.prefs, email }).then(({ data: p }) => { setData((d) => ({ ...d, prefs: p })); toast.success(email ? "Vous recevrez aussi les briefings par e-mail." : "Envoi par e-mail désactivé."); }).catch((e) => toast.error(errText(e)));
  const b = data?.briefing;
  const today = data?.today;
  return (
    <section className="bf-card" data-testid="ck-briefing">
      <header>
        <div className="bf-kinds">{KINDS.map(([k, l, Icon]) => <button key={k} type="button" className={kind === k ? "on" : ""} onClick={() => setKind(k)} data-testid={`ck-briefing-${k}`}><Icon size={14} /> {l}</button>)}</div>
        {kind !== "period" && <span className="bf-meta">{b ? `${b.date === today ? "Aujourd'hui" : new Date(b.date).toLocaleDateString("fr-FR")} · ${new Date(b.generated_at).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit", timeZone: "Europe/Paris" })} · ${b.user_name}` : "Pas encore généré"}</span>}
        {kind !== "period" && data?.prefs && <label className="bf-mail"><input type="checkbox" checked={data.prefs.email} onChange={(e) => setEmail(e.target.checked)} data-testid="ck-briefing-email" /><Mail size={13} /> Par e-mail</label>}
        {kind !== "period" && <button type="button" className="bf-gen" disabled={busy} onClick={generate} data-testid="ck-briefing-generate"><RefreshCw size={13} className={busy ? "spin" : ""} /> {busy ? "Rédaction…" : b ? "Régénérer" : "Générer maintenant"}</button>}
      </header>
      <div hidden={kind !== "period"}><PeriodBrief /></div>
      {kind === "period" ? null : data === null ? <Skeleton lines={4} testid="ck-briefing-loading" /> : !b ? <p className="bf-empty">{kind === "morning" ? "Le briefing est rédigé automatiquement chaque jour ouvré à l'heure réglée." : "Le bilan est rédigé automatiquement en fin de journée, ou à la demande."}</p> : (
        <div className="bf-body">
          {b.error && <p className="pl-warning">{b.error}</p>}
          {b.week && <WeekRecap week={b.week} />}
          <ul data-testid="ck-briefing-lines">{b.lines.map((l) => <li key={l}>{l}</li>)}</ul>
          {b.priorities?.length > 0 && (
            <ol className="bf-prios" data-testid="ck-briefing-priorities">
              <h4>Top {b.priorities.length} priorités du jour</h4>
              {b.priorities.map((p) => <li key={p.fiche_id}><Link to={`/pipeline?lead=${p.fiche_id}`} data-testid={`ck-briefing-prio-${p.fiche_id}`}><strong>{p.title}</strong><ArrowUpRight size={13} /></Link><span>{p.why}</span></li>)}
            </ol>
          )}
        </div>
      )}
    </section>
  );
};
